import { useListingStore } from '@/stores/listing/ListingStore';
import { getWhiteLabelState } from '@/stores/storeFacade';
import { getArtworkLabels } from '@/views/artworks/artwork-detail/utils';
import {
  type ArcualMediaItem,
  type ArcualSalesAgreement,
  type ArcualSalesAgreementProduct,
  CheckoutEventMessage,
  getAccountService,
  getSalesAgreementService,
  mediaUrl,
} from '@arcual/lib-app-platform';
import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const SalesAgreementStoreName = 'SalesAgreementStore';

export const useSalesAgreementStore = defineStore(SalesAgreementStoreName, () => {
  const { activationId } = getWhiteLabelState();
  const salesAgreementsList = ref<ArcualSalesAgreement[]>();

  const getPopulatedSalesAgreement = async (
    agreement: ArcualSalesAgreement,
    isGuest = false,
  ): Promise<ArcualSalesAgreement> => {
    if (agreement.buyerProfileId && !isGuest) {
      agreement.buyer = await getAccountService().getAccountProfileById(agreement.buyerProfileId);
    }

    agreement.seller = await getSalesAgreementService().getSalesAgreementSeller(agreement.id);

    if (agreement.artworks) {
      const errors: unknown[] = [];
      const settledArtworks = await Promise.allSettled(agreement.artworks.map(getSalesAgreementArtwork));
      const loadedArtworks = settledArtworks.reduce((result: ArcualSalesAgreementProduct[], settledPromise) => {
        if (settledPromise.status === 'fulfilled') {
          result.push(settledPromise.value);
        } else {
          errors.push(settledPromise.reason);
        }
        return result;
      }, []);
      if (errors.length > 0) {
        errors.forEach((e) => {
          console.error(e);
        });
      }
      loadedArtworks.forEach((loadedArtwork: ArcualSalesAgreementProduct) => {
        const index = agreement.artworks.findIndex((a) => a.id === loadedArtwork.id);
        if (index !== -1) {
          agreement.artworks[index].artwork = loadedArtwork.artwork;
        }
      });
      agreement.artworks.forEach((salesAgreement) => {
        if (salesAgreement.artwork) {
          // TODO: check image type once ArcualMediaItem supports url prop
          salesAgreement.artwork.images = salesAgreement.artwork.images.map((image: ArcualMediaItem) => ({
            ...image,
            id: mediaUrl(image.url, 2048, 2048),
          }));
        }
      });
    }

    return agreement;
  };

  const getSalesAgreementById = async (
    id: string,
    populateSalesAgreement = false,
    isGuest = false,
  ): Promise<ArcualSalesAgreement> => {
    try {
      const agreement = await getSalesAgreementService().getSalesAgreementById(id, isGuest);

      if (populateSalesAgreement) {
        return await getPopulatedSalesAgreement(agreement, isGuest);
      }
      return agreement;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const getSalesAgreementStatusById = async (id: string): Promise<Partial<ArcualSalesAgreement>> => {
    try {
      return await getSalesAgreementService().getSalesAgreementStatusById(id);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const getMultipleSalesAgreementsById = async (ids: string[]) => {
    try {
      return Promise.all(ids.map((id) => useSalesAgreementStore().getSalesAgreementById(id as string)));
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const getSalesAgreements = async (): Promise<ArcualSalesAgreement[]> => {
    const sa = await getSalesAgreementService().getSalesAgreementsByActivationId(activationId.value);
    // using allSettled here as sometimes individual artworks have issues right now, this way we still have the successfully loaded agreements
    // todo need to improve this
    const settledAgreements = await Promise.allSettled(sa.map((agreement: ArcualSalesAgreement) => getPopulatedSalesAgreement(agreement)));
    const errors: unknown[] = [];
    salesAgreementsList.value = settledAgreements.reduce((result: ArcualSalesAgreement[], settledPromise) => {
      if (settledPromise.status === 'fulfilled') {
        result.push(settledPromise.value);
      } else {
        errors.push(settledPromise.reason);
      }
      return result;
    }, []);
    if (errors.length > 0) {
      errors.forEach((e) => {
        console.error(e);
      });
    }
    return salesAgreementsList.value;
  };

  const getSalesAgreementArtwork = async (
    salesAgreementArtwork: ArcualSalesAgreementProduct,
  ): Promise<ArcualSalesAgreementProduct> => {
    const artwork = await useListingStore().getListing(salesAgreementArtwork.id);
    return {
      ...salesAgreementArtwork,
      artwork: {
        ...artwork,
        labels: getArtworkLabels(artwork),
      },
    };
  };

  const setupSseListener = async (
    abortController: AbortController,
    callback: (event: CheckoutEventMessage, salesAgreement: string) => void,
  ) =>
    getSalesAgreementService().salesAgreementSSEConnection(async (sseEventPayload: EventSourceMessage) => {
      console.debug('salesAgreementSSEConnection', sseEventPayload);

      /**
       * the response format
       * {
       *     "data": "{\"salesAgreementId\":\"c7177815-96c2-4d93-84d7-90bb960cd191\",\"userId\":\"5ea0990e-1c67-409a-81f5-4265af563f95\"}",
       *     "event": "payment-paid-sse-event",
       *     "id": "2024-09-26T21:31:13.757202370Z"
       * }
       */
      const convertedEvent = {
        type: sseEventPayload.event,
        data: JSON.parse(sseEventPayload.data),
      };
      const { type: event, data } = convertedEvent;
      const { salesAgreementId } = data;

      switch (event) {
        case CheckoutEventMessage.ACCEPTED:
        case CheckoutEventMessage.REJECTED:
          console.debug(`CHECKOUT EVENT -  ${salesAgreementId}`, event);
          callback(event, salesAgreementId);
          break;
        case CheckoutEventMessage.CREATED:
        case CheckoutEventMessage.CANCELLED:
        default:
          break;
      }
    }, abortController.signal);

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    salesAgreementsList.value = undefined;
  };

  return {
    salesAgreementsList,
    getSalesAgreements,
    getSalesAgreementById,
    getSalesAgreementStatusById,
    getMultipleSalesAgreementsById,
    setupSseListener,
    reset,
  };
});
